<div
  class="canva-connect-container tw-flex tw-flex-col"
  [ngClass]="{
    'has-toggle-visible': true,
  }"
>
  <div rvSpinner="canva-connect-loader" [rvShowSpinner]="spinner">
    @if (spinner) {
      <div class="spinner-overlay">
        <div class="spinner-border" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
    }

    <!-- Converting/Loading View - shown during import -->
    @else if (isImporting) {
      <div
        class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-flex-grow tw-p-2.5 rise-playlist-container"
      >
        <div
          class="tw-flex tw-items-center tw-justify-center tw-rounded-[10px]"
        >
          <mat-icon
            aria-hidden="true"
            [svgIcon]="selectedDesign?.type === 'video' ? 'zoom' : 'image'"
            class="tw-w-[30px] tw-h-[30px]"
          ></mat-icon>
        </div>

        <div class="tw-text-center tw-px-5">
          <h2 class="tw-text-[15px] tw-font-bold tw-mb-5 tw-mt-5">
            Converting your design...
          </h2>
          <p class="tw-text-[15px] tw-text-gray-600 tw-max-w-[280px] tw-mb-0">
            @if (uploadError) {
              <span class="tw-text-red-600">{{ uploadError }}</span>
            } @else {
              We're turning your Canva design into
              {{
                selectedDesign?.type === 'video'
                  ? 'a video file'
                  : selectedDesign?.type === 'document'
                    ? 'image files'
                    : 'an image file'
              }}. This may take a few moments.
            }
          </p>
        </div>

        <div class="tw-w-full tw-max-w-[280px] tw-px-5 tw-mt-5">
          <mat-progress-bar
            mode="determinate"
            [value]="overallProgress"
            class="rise-progress-bar"
          >
          </mat-progress-bar>
        </div>
      </div>
      <basic-uploader
        uploaderId="canva-connect-uploader"
        [uploadManager]="uploadManager"
        [validType]="validFileType"
        [fileList]="fileUploadList"
        [shown]="false"
      >
      </basic-uploader>
      <div class="tw-mt-auto tw-px-2.5 tw-pb-2.5 tw-bg-white">
        <button
          rv-button
          variant="secondary"
          class="tw-w-full"
          (click)="cancelImport()"
        >
          Cancel Import
        </button>
      </div>
    }

    <!-- Settings View - shown when design is already selected -->
    @else if (hasDesign()) {
      <div
        class="scrollable-component-container scrollable-component-settings te-scrollable-container tw-p-2.5 tw-mt-2.5"
      >
        <label class="tw-mb-[15px] tw-block">Connected account</label>
        <div
          class="attribute-editor-row tw-border-solid tw-border tw-border-gray-300 tw-rounded-[7.5px] tw-mb-[60px]"
        >
          <div
            class="tw-flex tw-items-center tw-gap-[7.5px] tw-p-[15px] tw-border-solid tw-border-0 tw-border-b tw-border-gray-300"
          >
            @if (connectedAccountDisplayName) {
              <div
                class="tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-rounded-[2.5px]"
              >
                <mat-icon
                  aria-hidden="true"
                  svgIcon="component-canva"
                  class="large"
                ></mat-icon>
              </div>
              <div class="tw-flex tw-flex-col tw-flex-1 tw-min-w-0">
                <span class="tw-text-[15px] tw-font-medium">{{
                  connectedAccountDisplayName
                }}</span>
              </div>
              <button
                rv-button
                variant="secondary"
                type="button"
                (click)="disconnect()"
                [disabled]="spinner"
              >
                Disconnect
              </button>
            } @else {
              <button
                matButton="outlined"
                class="tw-w-full tw-py-[25px]"
                (click)="authenticate()"
                [disabled]="spinner"
              >
                <div
                  class="tw-flex tw-items-center tw-justify-center tw-gap-2.5"
                >
                  <mat-icon
                    aria-hidden="true"
                    svgIcon="component-canva"
                    class="large"
                  ></mat-icon>
                  Connect to Canva
                </div>
              </button>
            }
          </div>
          <div class="tw-flex tw-items-center tw-gap-[7.5px] tw-p-[15px]">
            <div
              class="tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-rounded-[2.5px]"
            >
              <mat-icon
                aria-hidden="true"
                [svgIcon]="selectedDesign?.type === 'video' ? 'zoom' : 'image'"
                class="large"
              ></mat-icon>
            </div>
            <div class="tw-flex tw-flex-col tw-flex-1 tw-min-w-0">
              <span class="tw-text-[15px] tw-font-bold tw-truncate">{{
                selectedDesign.name
              }}</span>
              <span class="tw-text-[12.5px] tw-text-gray-500">{{
                selectedDesign.type | uppercase
              }}</span>
            </div>
            <button
              [matMenuTriggerFor]="designMenu"
              class="tw-bg-transparent tw-border-0 tw-p-[5px] tw-cursor-pointer tw-text-gray-500 hover:tw-text-gray-700"
              aria-label="Design options"
            >
              <streamline-icon
                aria-hidden="true"
                name="ellipsis"
                width="16"
                height="16"
              ></streamline-icon>
            </button>
            <mat-menu #designMenu="matMenu">
              @if (canEditInCanva()) {
                <button
                  mat-menu-item
                  [disabled]="selectedDesign?.templateDefault"
                  (click)="editInCanva()"
                >
                  <mat-icon
                    aria-hidden="true"
                    svgIcon="component-canva"
                    class="default"
                  ></mat-icon>
                  <span>Edit in Canva</span>
                </button>
              }
              <button mat-menu-item (click)="selectDesign()">
                <mat-icon
                  aria-hidden="true"
                  svgIcon="file"
                  class="default"
                ></mat-icon>
                <span>{{
                  selectedDesign?.templateDefault
                    ? 'Select a file'
                    : 'Change file'
                }}</span>
              </button>
            </mat-menu>
          </div>
        </div>

        <div class="attribute-editor-row tw-mb-[30px]">
          <div class="tw-flex tw-items-center tw-justify-between tw-mb-[15px]">
            <label class="tw-m-0">Refresh design</label>
            <button rv-button variant="secondary" (click)="refreshDesign()">
              <mat-icon aria-hidden="true" svgIcon="refresh"></mat-icon>
              Refresh
            </button>
          </div>
          <mat-form-field
            appearance="outline"
            subscriptSizing="dynamic"
            class="tw-w-full tw-mt-[5px]"
          >
            <mat-select
              [(ngModel)]="refreshInterval"
              (selectionChange)="saveRefreshInterval()"
              aria-label="Refresh interval"
            >
              <mat-option [value]="60">1 hour</mat-option>
              <mat-option [value]="180">3 hours</mat-option>
              <mat-option [value]="360">6 hours</mat-option>
              <mat-option [value]="720">12 hours</mat-option>
            </mat-select>
          </mat-form-field>
          <p class="help-block tw-mt-[5px]">
            Choose how often this design updates automatically
          </p>
        </div>
        <div class="attribute-editor-row" [hidden]="validFileType !== 'video'">
          <div id="folder-component-volume">
            <label>Video Volume:</label>
            <div class="w-100 flex flex-row gap-3">
              <mat-icon svgIcon="volume" class="mr-1 large"></mat-icon>
              <mat-slider
                min="0"
                max="100"
                step="1"
                color="accent"
                aria-label="Video volume control"
                class="flex-grow"
              >
                <input
                  matSliderThumb
                  [(ngModel)]="volume"
                  (valueChange)="saveVolume()"
                  [attr.aria-valuetext]="volume + ' percent volume'"
                />
              </mat-slider>
              <div class="volume">{{ volume }}</div>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Authentication UI when not authorized and no design -->
    @else if (!userAuthorized) {
      <div class="tw-mt-[60px]">
        <div class="text-center p-4">
          <h4>Connect to Canva</h4>
          <p class="tw-text-gray-700 tw-py-[5px]">
            Link your Canva account to access your designs directly in Rise
            Vision.
          </p>

          <button
            matButton="outlined"
            class="tw-w-full button-role--provider"
            (click)="authenticate()"
            [disabled]="spinner"
          >
            <mat-icon
              aria-hidden="true"
              svgIcon="component-canva"
              class="large"
            ></mat-icon>
            Connect to Canva
          </button>

          @if (authenticateFailed) {
            <p class="tw-text-red-600 tw-pt-[15px]">
              We couldn&rsquo;t connect to Canva. Try again.
            </p>
          } @else {
            <p class="tw-text-gray-500 tw-pt-[15px]">
              You&rsquo;ll be redirected to Canva to sign in securely.
            </p>
          }
        </div>
      </div>
    }

    <!-- Grid View - shown when selecting a design (authorized and no design) -->
    @else {
      <!-- Search UI -->
      <div class="tw-px-2.5 tw-pt-2.5 tw-pb-[5px]">
        <search-filter
          [filterConfig]="filterConfig"
          [search]="search"
          [doSearch]="doSearch"
          [localSearch]="false"
        ></search-filter>
      </div>

      <!-- Scrollable designs grid -->
      <div
        class="attribute-editor-row te-scrollable-container rise-playlist-container"
        scrollingList
        (scrollEndEvent)="load()"
      >
        @if (loadingDesigns && designs.length === 0) {
          <div
            class="tw-flex tw-items-center tw-justify-center tw-w-full tw-py-5"
          >
            <p class="tw-text-gray-500">Loading designs...</p>
          </div>
        } @else if (designs.length === 0 && !loadingDesigns) {
          <div
            class="tw-flex tw-items-center tw-justify-center tw-w-full tw-py-5"
          >
            <p class="tw-text-gray-500">No designs found</p>
          </div>
        } @else {
          <ul
            class="tw-list-none tw-p-0 tw-m-0 tw-w-full tw-flex tw-flex-wrap tw-gap-2.5 tw-py-2.5 tw-px-2.5 tw-justify-center"
          >
            @for (design of designs; track design.id; let i = $index) {
              <li class="canva-design-item-wrapper">
                <a
                  role="button"
                  tabindex="0"
                  [attr.aria-label]="'Canva design ' + design.title"
                  [attr.aria-pressed]="isSelected(i)"
                  class="canva-design-item"
                  [ngClass]="{ selected: isSelected(i) }"
                  (click)="onPlaceholderClick(i)"
                  (keydown.space)="
                    $event.preventDefault(); onPlaceholderClick(i)
                  "
                  (keydown.enter)="onPlaceholderClick(i)"
                >
                  @if (design.thumbnail?.url) {
                    <img
                      [src]="design.thumbnail.url"
                      [alt]="design.title"
                      class="canva-design-thumbnail"
                    />
                  } @else {
                    <span
                      class="canva-design-placeholder-text"
                      [ngClass]="{ selected: isSelected(i) }"
                      >{{ i + 1 }}</span
                    >
                  }
                </a>
                <div class="tw-flex tw-flex-col tw-justify-between tw-mt-[5px]">
                  <span class="tw-text-[15px] tw-font-bold">{{
                    design.title || 'Untitled Design'
                  }}</span>
                </div>
              </li>
            }

            <!-- Loading indicator for pagination -->
            @if (loadingDesigns && designs.length > 0) {
              <li
                class="tw-w-full tw-flex tw-items-center tw-justify-center tw-py-5"
              >
                <div class="spinner-border spinner-border-sm" role="status">
                  <span class="sr-only">Loading more designs...</span>
                </div>
                <span class="tw-ml-[5px] tw-text-gray-500"
                  >Loading more designs...</span
                >
              </li>
            }
          </ul>
        }
      </div>

      <!-- Action button bar -->
      <div
        class="canva-connect-action-button-bar"
        (click)="$event.stopPropagation()"
        (mousedown)="$event.stopPropagation()"
      >
        <div class="tw-border-t">
          <!-- Export as Video toggle -->
          <div
            class="tw-flex tw-items-center tw-justify-between tw-mb-2.5 tw-px-[2.5px]"
            (click)="$event.stopPropagation()"
            (mousedown)="$event.stopPropagation()"
          >
            <div class="tw-flex tw-flex-col">
              <span class="tw-text-[13px] tw-font-medium">Import as Video</span>
            </div>
            <mat-slide-toggle
              [ngModel]="exportAsVideo"
              (ngModelChange)="saveExportAsVideo($event)"
              [disabled]="
                selectedPlaceholder === null ||
                loadingExportFormats ||
                !selectedExportFormats?.mp4 ||
                designs[selectedPlaceholder]?.page_count > 1
              "
              color="primary"
              aria-label="Import as video toggle"
              (click)="$event.stopPropagation()"
              (mousedown)="$event.stopPropagation()"
            ></mat-slide-toggle>
          </div>
          <button
            rv-button
            id="canva-connect-import-button"
            class="tw-w-full"
            (click)="onImportFileClick()"
            [disabled]="
              selectedPlaceholder === null ||
              loadingExportFormats ||
              !exportFormat
            "
          >
            <strong>{{ getImportButtonText() }}</strong>
            @if (loadingExportFormats) {
              <mat-spinner diameter="20" class="tw-ml-[5px]"></mat-spinner>
            }
          </button>
        </div>
      </div>
    }
  </div>
</div>
